import React, { useState } from "react";

interface TrafficMetric {
  label: string;
  value: string | number;
  icon: string;
  subtext?: string;
  change?: { percent: number; isPositive: boolean };
  unit?: string;
}

interface FloorData {
  floor: string;
  stops: number;
}

interface DensityData {
  floor: string;
  in: number;
  out: number;
}

interface ElevatorMetrics {
  id: string;
  name: string;
  totalTrips: number;
  avgResponseTime: number;
  efficiency: number;
  uptime: number;
  status: "good" | "warning" | "alert";
}

interface HourlyData {
  hour: string;
  efficiency: number;
}

interface PerformanceMetrics {
  doorOperationTime: number;
  averageWaitTime: number;
  jerkFactor: number;
  systemAvailability: number;
  maintenanceHealth: number;
}

interface FloorTrafficData {
  floor: string;
  traffic: number;
}

interface DwellTimeData {
  floor: string;
  dwell: number;
}

export type TrafficRecord = {
  elevatorId: string;
  floor: number;
  peopleIn: number;
  peopleOut: number;
  peopleInElevator: number;
  dateTime: string;
};

type TrafficInformationViewProps = {
  elevatorTrafficData: TrafficRecord[];
  elevatorCapacity: number;
};

export default function TrafficInformationView({ elevatorTrafficData, elevatorCapacity }: TrafficInformationViewProps) {
  const [view, setView] = useState<"simplified" | "detailed">("simplified");
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [filterDateFrom, setFilterDateFrom] = useState("2026-03-25T00:00");
  const [filterDateTo, setFilterDateTo] = useState("2026-03-25T23:59");
  const [filterElevator, setFilterElevator] = useState<string>("all");
  const [filterFloor, setFilterFloor] = useState<string>("all");
  const [visibleRows, setVisibleRows] = useState(5);

  const ELEVATOR_CAPACITY = elevatorCapacity;

  const handleExport = () => {
    const filtered = getFilteredTrafficData()
      .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
    const csvRows = ["Date & Time,Elevator,Floor,People In,People Out,In Elevator,Status"];
    filtered.forEach((r) => {
      const isCritical = r.peopleInElevator > ELEVATOR_CAPACITY;
      const isWarning = r.peopleInElevator > ELEVATOR_CAPACITY * 0.9;
      const status = isCritical ? "OVERLOAD" : isWarning ? "HIGH" : "OK";
      csvRows.push(`"${new Date(r.dateTime).toLocaleString()}","${r.elevatorId}",${r.floor},${r.peopleIn},${r.peopleOut},${r.peopleInElevator},${status}`);
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `traffic-log-${filterDateFrom.slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getFilteredTrafficData = () => {
    return elevatorTrafficData.filter((r) => {
      const dt = new Date(r.dateTime);
      const from = new Date(filterDateFrom);
      const to = new Date(filterDateTo);
      if (dt < from || dt > to) return false;
      if (filterElevator !== "all" && r.elevatorId !== filterElevator) return false;
      if (filterFloor !== "all" && r.floor !== Number(filterFloor)) return false;
      return true;
    });
  };

  const [trafficData] = useState({
    totalTripsToday: 9000,
    totalTripsYesterday: 8036,
    peakHour: "5 PM",
    peakHourTrips: 450,
    avgWaitTime: 28,
    busiestFloor: "Ground",
    busiestFloorTrips: 2845,
    floorData: [
      { floor: "Floor 1", stops: 2300 },
      { floor: "Floor 2", stops: 1500 },
      { floor: "Floor 3", stops: 1900 },
      { floor: "Floor 4", stops: 1800 },
      { floor: "Floor 5", stops: 1200 },
      { floor: "Floor 6", stops: 900 },
    ] as FloorData[],
    topPerformer: {
      floor: 4,
      elevatorId: "#1",
      totalStops: 1790,
    },
    densityData: [
      { floor: "Ground", in: 450, out: 380 },
      { floor: "Floor 2", in: 320, out: 290 },
      { floor: "Floor 4", in: 410, out: 425 },
      { floor: "Floor 6", in: 280, out: 310 },
    ] as DensityData[],
    elevators: [
      {
        id: "1",
        name: "Elevator 1",
        totalTrips: 892,
        avgResponseTime: 24,
        efficiency: 78,
        uptime: 99.2,
        status: "good" as const,
      },
      {
        id: "2",
        name: "Elevator 2",
        totalTrips: 856,
        avgResponseTime: 26,
        efficiency: 75,
        uptime: 98.8,
        status: "good" as const,
      },
      {
        id: "3",
        name: "Elevator 3",
        totalTrips: 734,
        avgResponseTime: 28,
        efficiency: 72,
        uptime: 97.5,
        status: "warning" as const,
      },
      {
        id: "4",
        name: "Elevator 4",
        totalTrips: 805,
        avgResponseTime: 27,
        efficiency: 76,
        uptime: 99.0,
        status: "good" as const,
      },
    ] as ElevatorMetrics[],
    elevatorStopsData: {
      headers: ["Elevator", "F1", "F2", "F3", "F4", "F5", "F6", "Total"],
      rows: [
        { name: "Elevator 1", floors: [425, 325, 230, 290, 240, 182, 892] },
        { name: "Elevator 2", floors: [200, 300, 180, 175, 156, 122, 851] },
        { name: "Elevator 3", floors: [180, 200, 150, 145, 130, 129, 734] },
        { name: "Elevator 4", floors: [290, 280, 340, 190, 174, 165, 805] },
      ],
    },
    hourlyData: [
      { hour: "12am", efficiency: 45 },
      { hour: "2am", efficiency: 42 },
      { hour: "4am", efficiency: 40 },
      { hour: "6am", efficiency: 55 },
      { hour: "8am", efficiency: 78 },
      { hour: "10am", efficiency: 85 },
      { hour: "12pm", efficiency: 92 },
      { hour: "2pm", efficiency: 88 },
      { hour: "4pm", efficiency: 95 },
      { hour: "6pm", efficiency: 87 },
      { hour: "8pm", efficiency: 72 },
      { hour: "10pm", efficiency: 58 },
    ] as HourlyData[],
    performanceMetrics: {
      doorOperationTime: 88,
      averageWaitTime: 82,
      jerkFactor: 76,
      systemAvailability: 99.2,
      maintenanceHealth: 91,
    } as PerformanceMetrics,
    floorTrafficData: [
      { floor: "Ground", traffic: 450 },
      { floor: "Floor 1", traffic: 380 },
      { floor: "Floor 2", traffic: 320 },
      { floor: "Floor 3", traffic: 290 },
      { floor: "Floor 4", traffic: 425 },
      { floor: "Floor 5", traffic: 310 },
      { floor: "Floor 6", traffic: 210 },
    ] as FloorTrafficData[],
    dwellTimeData: [
      { floor: "Ground", dwell: 12 },
      { floor: "Floor 1", dwell: 8 },
      { floor: "Floor 2", dwell: 6 },
      { floor: "Floor 3", dwell: 7 },
      { floor: "Floor 4", dwell: 9 },
      { floor: "Floor 5", dwell: 5 },
      { floor: "Floor 6", dwell: 4 },
    ] as DwellTimeData[],
    populationDensity: [
      { floor: "Ground", people: 245, capacity: 300, occupancyPercent: 82 },
      { floor: "Floor 1", people: 156, capacity: 250, occupancyPercent: 62 },
      { floor: "Floor 2", people: 189, capacity: 280, occupancyPercent: 68 },
      { floor: "Floor 3", people: 203, capacity: 300, occupancyPercent: 68 },
      { floor: "Floor 4", people: 312, capacity: 350, occupancyPercent: 89 },
      { floor: "Floor 5", people: 178, capacity: 250, occupancyPercent: 71 },
      { floor: "Floor 6", people: 98, capacity: 200, occupancyPercent: 49 },
    ],
    flowData: [
      { floor: "Ground", in: 245, out: 12, netChange: 233 },
      { floor: "Floor 1", in: 156, out: 89, netChange: 67 },
      { floor: "Floor 2", in: 189, out: 145, netChange: 44 },
      { floor: "Floor 3", in: 203, out: 178, netChange: 25 },
      { floor: "Floor 4", in: 312, out: 267, netChange: 45 },
      { floor: "Floor 5", in: 178, out: 142, netChange: 36 },
      { floor: "Floor 6", in: 98, out: 78, netChange: 20 },
    ],
  });

  const getMaxStops = () => {
    return Math.max(...trafficData.floorData.map((d) => d.stops));
  };

  const getMaxFloorTraffic = () => {
    return Math.max(...trafficData.floorTrafficData.map((d) => d.traffic));
  };

  const getMaxDwellTime = () => {
    return Math.max(...trafficData.dwellTimeData.map((d) => d.dwell));
  };

  const maxStops = getMaxStops();
  const maxFloorTraffic = getMaxFloorTraffic();
  const maxDwellTime = getMaxDwellTime();

  const computedMetrics = (() => {
    const filtered = getFilteredTrafficData();
    let totalTrips = 0;
    let peakHourLabel = "—";
    let peakHourTrips = 0;
    let busiestElevatorName = "—";
    let busiestElevatorTrips = 0;
    let busiestFloorName = "—";
    let busiestFloorTrips = 0;

    if (filtered.length > 0) {
      // Find the latest record by dateTime
      const latest = filtered.reduce((a, b) => new Date(a.dateTime) > new Date(b.dateTime) ? a : b);
      // For total trips today: sum the latest peopleIn + peopleOut for each unique elevator
      const elevatorMapForTotal: Record<string, { sum: number, record: typeof latest | null }> = {};
      filtered.forEach((rec) => {
        if (!elevatorMapForTotal[rec.elevatorId] || new Date(rec.dateTime) > new Date(elevatorMapForTotal[rec.elevatorId].record!.dateTime)) {
          elevatorMapForTotal[rec.elevatorId] = { sum: rec.peopleIn + rec.peopleOut, record: rec };
        }
      });
      totalTrips = Object.values(elevatorMapForTotal).reduce((acc, val) => acc + val.sum, 0);

      // Peak hour: for each hour, find the latest record for each elevator in that hour, sum peopleIn + peopleOut for those records, and find the hour with the highest sum
      const hourElevatorMap: Record<number, Record<string, { sum: number, record: typeof latest | null }>> = {};
      filtered.forEach((rec) => {
        const hour = new Date(rec.dateTime).getHours();
        if (!hourElevatorMap[hour]) hourElevatorMap[hour] = {};
        if (!hourElevatorMap[hour][rec.elevatorId] || new Date(rec.dateTime) > new Date(hourElevatorMap[hour][rec.elevatorId].record!.dateTime)) {
          hourElevatorMap[hour][rec.elevatorId] = { sum: rec.peopleIn + rec.peopleOut, record: rec };
        }
      });
      // For each hour, sum the latest peopleIn + peopleOut for each elevator
      const hourSums: { hour: number, sum: number }[] = Object.entries(hourElevatorMap).map(([hour, elevatorMap]) => ({
        hour: Number(hour),
        sum: Object.values(elevatorMap).reduce((acc, val) => acc + val.sum, 0)
      }));
      hourSums.sort((a, b) => b.sum - a.sum);
      if (hourSums.length > 0) {
        const peakHourNum = hourSums[0].hour;
        peakHourLabel = `${peakHourNum === 0 ? 12 : peakHourNum > 12 ? peakHourNum - 12 : peakHourNum} ${peakHourNum >= 12 ? "PM" : "AM"}`;
        peakHourTrips = hourSums[0].sum;
      }

      // Busiest elevator: find elevatorId with highest people in + out (using latest record for each elevator) -- always use all elevators, not filtered
      const elevatorMap: Record<string, { sum: number, record: TrafficRecord | null }> = {};
      elevatorTrafficData.forEach((rec) => {
        if (!elevatorMap[rec.elevatorId] || new Date(rec.dateTime) > new Date(elevatorMap[rec.elevatorId].record!.dateTime)) {
          elevatorMap[rec.elevatorId] = { sum: rec.peopleIn + rec.peopleOut, record: rec };
        }
      });
      const busiestElevatorEntry = Object.entries(elevatorMap).sort((a, b) => b[1].sum - a[1].sum)[0];
      if (busiestElevatorEntry) {
        busiestElevatorName = busiestElevatorEntry[0];
        busiestElevatorTrips = busiestElevatorEntry[1].sum;
      }

      // Busiest floor: find the floor with the highest total number of visits (number of records per floor)
      const floorVisitCounts: Record<number, number> = {};
      elevatorTrafficData.forEach((rec) => {
        floorVisitCounts[rec.floor] = (floorVisitCounts[rec.floor] || 0) + 1;
      });
      const busiestFloorVisitEntry = Object.entries(floorVisitCounts).sort((a, b) => b[1] - a[1])[0];
      if (busiestFloorVisitEntry) {
        busiestFloorName = `Floor ${busiestFloorVisitEntry[0]}`;
        busiestFloorTrips = busiestFloorVisitEntry[1];
      }
    }

    return { totalTrips, peakHourLabel, peakHourTrips, busiestElevatorName, busiestElevatorTrips, busiestFloorName, busiestFloorTrips };
  })();

  const metrics: TrafficMetric[] = [
    {
      label: "Total Trips Today",
      value: computedMetrics.totalTrips.toLocaleString(),
      icon: "📈",
      subtext: "people in + out",
    },
    {
      label: "Peak Hour",
      value: computedMetrics.peakHourLabel,
      icon: "⏰",
      subtext: `${computedMetrics.peakHourTrips.toLocaleString()} movements`,
    },
    {
      label: "Busiest Elevator",
      value: computedMetrics.busiestElevatorName,
      icon: "🛗",
      subtext: `${computedMetrics.busiestElevatorTrips.toLocaleString()} movements`,
    },
    {
      label: "Busiest Floor",
      value: computedMetrics.busiestFloorName,
      icon: "📍",
      subtext: `${computedMetrics.busiestFloorTrips.toLocaleString()} movements`,
    },
  ];

  // Radar chart drawing function
  const drawRadarChart = () => {
    const metrics = trafficData.performanceMetrics;
    const metricsArray = [
      { label: "Door Operation", value: metrics.doorOperationTime },
      { label: "Avg Wait Time", value: metrics.averageWaitTime },
      { label: "Jerk Factor", value: metrics.jerkFactor },
      { label: "System Availability", value: metrics.systemAvailability > 100 ? 100 : metrics.systemAvailability },
      { label: "Maintenance Health", value: metrics.maintenanceHealth },
    ];

    const size = 200;
    const center = size / 2;
    const radius = size / 2.5;
    const levels = 5;

    let polygonPoints = "";
    let dataPoints = "";

    metricsArray.forEach((metric, i) => {
      const angle = (i * 2 * Math.PI) / metricsArray.length - Math.PI / 2;
      const x = center + radius * Math.cos(angle);
      const y = center + radius * Math.sin(angle);
      const dataX = center + (radius * metric.value) / 100 * Math.cos(angle);
      const dataY = center + (radius * metric.value) / 100 * Math.sin(angle);

      polygonPoints += `${x},${y} `;
      dataPoints += `${dataX},${dataY} `;
    });

    return (
      <svg width="240" height="240" viewBox="0 0 200 200" className="mx-auto">
        <defs>
          <linearGradient id="radarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.3" />
          </linearGradient>
        </defs>

        {/* Grid circles */}
        {[...Array(levels)].map((_, i) => (
          <circle
            key={`level-${i}`}
            cx="100"
            cy="100"
            r={(radius * (i + 1)) / levels}
            fill="none"
            stroke="rgba(148, 163, 184, 0.2)"
            strokeWidth="1"
          />
        ))}

        {/* Axes */}
        {metricsArray.map((_, i) => {
          const angle = (i * 2 * Math.PI) / metricsArray.length - Math.PI / 2;
          const x = 100 + radius * Math.cos(angle);
          const y = 100 + radius * Math.sin(angle);
          return (
            <line
              key={`axis-${i}`}
              x1="100"
              y1="100"
              x2={x}
              y2={y}
              stroke="rgba(148, 163, 184, 0.15)"
              strokeWidth="1"
            />
          );
        })}

        {/* Data polygon */}
        <polygon
          points={dataPoints}
          fill="url(#radarGradient)"
          stroke="#06b6d4"
          strokeWidth="2"
        />

        {/* Labels */}
        {metricsArray.map((metric, i) => {
          const angle = (i * 2 * Math.PI) / metricsArray.length - Math.PI / 2;
          const x = 100 + (radius + 30) * Math.cos(angle);
          const y = 100 + (radius + 30) * Math.sin(angle);
          return (
            <text
              key={`label-${i}`}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-xs fill-slate-300"
              fontSize="10"
            >
              {metric.value}%
            </text>
          );
        })}
      </svg>
    );
  };

  // Line chart for hourly performance
  const drawLineChart = () => {
    const width = 500;
    const height = 200;
    const padding = 40;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    const maxValue = 100;
    const points = trafficData.hourlyData.map((data, i) => {
      const x = padding + (i / (trafficData.hourlyData.length - 1)) * chartWidth;
      const y = padding + chartHeight - (data.efficiency / maxValue) * chartHeight;
      return { x, y, data };
    });

    const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(" ");

    return (
      <div className="relative w-full">
        <svg 
          width="100%" 
          height="240" 
          viewBox={`0 0 ${width} ${height}`} 
          className="w-full"
          onMouseLeave={() => setHoveredPoint(null)}
        >
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Grid lines */}
          {[...Array(5)].map((_, i) => (
            <line
              key={`grid-${i}`}
              x1={padding}
              y1={padding + (i * chartHeight) / 4}
              x2={width - padding}
              y2={padding + (i * chartHeight) / 4}
              stroke="rgba(148, 163, 184, 0.1)"
              strokeWidth="1"
            />
          ))}

          {/* Axes */}
          <line
            x1={padding}
            y1={padding}
            x2={padding}
            y2={height - padding}
            stroke="rgba(148, 163, 184, 0.3)"
            strokeWidth="2"
          />
          <line
            x1={padding}
            y1={height - padding}
            x2={width - padding}
            y2={height - padding}
            stroke="rgba(148, 163, 184, 0.3)"
            strokeWidth="2"
          />

          {/* Fill area under line */}
          <polyline
            points={polylinePoints}
            fill="none"
            stroke="url(#lineGradient)"
            strokeWidth="2"
          />

          {/* Main line */}
          <polyline
            points={polylinePoints}
            fill="none"
            stroke="#06b6d4"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#glow)"
          />

          {/* Vertical line at hover */}
          {hoveredPoint !== null && (
            <>
              <line
                x1={points[hoveredPoint].x}
                y1={padding}
                x2={points[hoveredPoint].x}
                y2={height - padding}
                stroke="rgba(6, 182, 212, 0.5)"
                strokeWidth="2"
                strokeDasharray="4"
              />
              <circle
                cx={points[hoveredPoint].x}
                cy={points[hoveredPoint].y}
                r="6"
                fill="#06b6d4"
                stroke="#fff"
                strokeWidth="2"
                filter="url(#glow)"
              />
            </>
          )}

          {/* Data points - interactive */}
          {points.map((point, i) => {
            const isHovered = i === hoveredPoint;
            return (
              <g key={`point-group-${i}`}>
                {/* Invisible larger circle for better hover area */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="10"
                  fill="transparent"
                  cursor="pointer"
                  onMouseEnter={() => setHoveredPoint(i)}
                  className="transition-all"
                />
                {/* Visible circle */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={isHovered ? 5 : 3}
                  fill={isHovered ? "#10b981" : "#06b6d4"}
                  stroke="#fff"
                  strokeWidth={isHovered ? 2 : 1}
                  className="transition-all duration-200"
                  filter={isHovered ? "url(#glow)" : ""}
                />
              </g>
            );
          })}

          {/* X axis labels */}
          {trafficData.hourlyData.map((data, i) => {
            if (i % 2 === 0) {
              const x = padding + (i / (trafficData.hourlyData.length - 1)) * chartWidth;
              return (
                <text
                  key={`label-${i}`}
                  x={x}
                  y={height - padding + 20}
                  textAnchor="middle"
                  className="text-xs fill-slate-400"
                  fontSize="11"
                >
                  {data.hour}
                </text>
              );
            }
          })}

          {/* Y axis labels */}
          {[0, 25, 50, 75, 100].map((value, i) => (
            <text
              key={`y-label-${i}`}
              x={padding - 10}
              y={padding + chartHeight - (value / 100) * chartHeight + 4}
              textAnchor="end"
              className="text-xs fill-slate-400"
              fontSize="10"
            >
              {value}%
            </text>
          ))}
        </svg>

        {/* Tooltip */}
        {hoveredPoint !== null && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 bg-slate-900 border border-cyan-500/50 rounded-lg px-4 py-3 shadow-xl backdrop-blur-sm">
            <div className="text-center">
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">
                {trafficData.hourlyData[hoveredPoint].hour}
              </p>
              <p className="text-2xl font-bold text-cyan-300 mt-1">
                {trafficData.hourlyData[hoveredPoint].efficiency}%
              </p>
              <p className="text-xs text-slate-400 mt-1">Efficiency</p>
            </div>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900"></div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-4 flex items-center justify-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
            <span className="text-xs text-slate-400">Efficiency Trend</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-xs text-slate-400">Hover to Inspect</span>
          </div>
        </div>
      </div>
    );
  };

  const ElevatorVisualization = () => {
    const [elevatorStates, setElevatorStates] = useState([
      { position: 0, progress: 0 }, // 0-1, where 0.5 is at the top
      { position: 0.25, progress: 0.25 },
      { position: 0.5, progress: 0.5 },
      { position: 0.75, progress: 0.75 },
    ]);

    React.useEffect(() => {
      let animationFrameId: NodeJS.Timeout;
      
      const animate = () => {
        setElevatorStates((prev) =>
          prev.map((state) => {
            let newProgress = state.progress + 0.005;
            if (newProgress > 1) newProgress = 0;
            return { ...state, progress: newProgress };
          })
        );
        animationFrameId = setTimeout(animate, 100);
      };

      animationFrameId = setTimeout(animate, 100);
      return () => clearTimeout(animationFrameId);
    }, []);

    const getFloorAndDirection = (progress: number) => {
      // Progress 0-0.5: going UP (floor 1 to 10)
      // Progress 0.5-1: going DOWN (floor 10 to 1)
      if (progress <= 0.5) {
        const floor = Math.ceil(1 + (progress / 0.5) * 9);
        return { floor: Math.min(floor, 10), direction: 'up' as const };
      } else {
        const floor = Math.ceil(10 - ((progress - 0.5) / 0.5) * 9);
        return { floor: Math.max(floor, 1), direction: 'down' as const };
      }
    };

    const getYPosition = (progress: number) => {
      // Shaft height is 320px (4 floors with 80px per floor)
      // Floor 10 = 0px (top), Floor 1 = 280px (bottom)
      if (progress <= 0.5) {
        // Going up: 280px to 0px
        return 280 - (progress / 0.5) * 280;
      } else {
        // Going down: 0px to 280px
        return ((progress - 0.5) / 0.5) * 280;
      }
    };

    const elevatorClass = ['elevator-e1', 'elevator-e2', 'elevator-e3', 'elevator-e4'];
    const colorGradient = [
      'from-cyan-500 to-blue-400',
      'from-blue-500 to-cyan-400',
      'from-teal-500 to-cyan-400',
      'from-sky-500 to-blue-400'
    ];

    return (
      <div className="flex gap-4">
        {/* Floor Labels */}
        <div className="flex flex-col justify-between pt-2" style={{ height: '320px' }}>
          <span className="text-xs font-semibold text-slate-400">10</span>
          <span className="text-xs font-semibold text-slate-400">7</span>
          <span className="text-xs font-semibold text-slate-400">4</span>
          <span className="text-xs font-semibold text-slate-400">1</span>
        </div>

        {/* Elevator Shafts */}
        <div className="flex gap-6 flex-1">
          {[0, 1, 2, 3].map((idx) => {
            const { floor, direction } = getFloorAndDirection(elevatorStates[idx].progress);
            const yPos = getYPosition(elevatorStates[idx].progress);
            
            return (
              <div key={idx} className="flex flex-col flex-1">
                {/* Shaft */}
                <div className="relative w-full bg-gradient-to-b from-slate-800/60 to-slate-900/60 border-2 border-cyan-500/30 rounded-lg" style={{ height: '320px', overflow: 'hidden' }}>
                  {/* Floor grid lines */}
                  <div className="absolute inset-0 flex flex-col">
                    <div className="flex-1 border-b border-slate-700/40"></div>
                    <div className="flex-1 border-b border-slate-700/40"></div>
                    <div className="flex-1 border-b border-slate-700/40"></div>
                    <div className="flex-1"></div>
                  </div>

                  {/* Elevator car */}
                  <div
                    className={`absolute left-2 right-2 h-12 rounded-md bg-gradient-to-r ${colorGradient[idx]} shadow-lg flex items-center justify-center transition-none`}
                    style={{ 
                      width: 'calc(100% - 16px)',
                      top: `${yPos}px`,
                    }}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-sm font-bold text-white">{floor}</span>
                      <span className="text-lg leading-none">
                        {direction === 'up' ? '↑' : '↓'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Status */}
                <div className="mt-2 text-center">
                  <span className="text-xs font-semibold text-cyan-300">E{idx + 1}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (view === "detailed") {
    return (
      <div className="w-full h-full bg-slate-900/50">
        {/* Header */}
        <div className="border-b border-slate-700/50 px-6 py-4 sticky top-0 z-10 bg-slate-900/80 backdrop-blur">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-white">
              Elevator 1 - Detailed Analytics
            </h1>
            <div className="flex gap-2 bg-slate-800/50 p-1 rounded-lg border border-slate-700/50">
              <button
                onClick={() => setView("simplified")}
                className="px-4 py-1.5 text-slate-400 hover:text-white rounded font-medium text-sm transition"
              >
                Simplified
              </button>
              <button className="px-4 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded font-medium text-sm">
                Detailed
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6 overflow-auto">
          {/* Advanced Metrics */}
          <style>{`
            @keyframes elevatorE1 {
              0% { top: 280px; }
              25% { top: 200px; }
              50% { top: 80px; }
              75% { top: 160px; }
              100% { top: 280px; }
            }
            @keyframes elevatorE2 {
              0% { top: 160px; }
              25% { top: 80px; }
              50% { top: 240px; }
              75% { top: 200px; }
              100% { top: 160px; }
            }
            @keyframes elevatorE3 {
              0% { top: 80px; }
              25% { top: 200px; }
              50% { top: 160px; }
              75% { top: 280px; }
              100% { top: 80px; }
            }
            @keyframes elevatorE4 {
              0% { top: 200px; }
              25% { top: 280px; }
              50% { top: 120px; }
              75% { top: 80px; }
              100% { top: 200px; }
            }
            .elevator-e1 { animation: elevatorE1 6s ease-in-out infinite; }
            .elevator-e2 { animation: elevatorE2 6s ease-in-out infinite; }
            .elevator-e3 { animation: elevatorE3 6s ease-in-out infinite; }
            .elevator-e4 { animation: elevatorE4 6s ease-in-out infinite; }
          `}</style>
          
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-6">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-700/50">
              <h2 className="text-lg font-bold text-white">Real-Time Elevator Movement</h2>
              <span className="text-2xl">🏢</span>
            </div>
            
            {/* Building Schematic */}
            <ElevatorVisualization />
            
            <div className="mt-6 pt-4 border-t border-slate-700/50">
              <p className="text-xs text-slate-300">
                <span className="text-cyan-300 font-semibold">🔄 Live Movement:</span> Each elevator is continuously servicing floors. The vertical position indicates current floor, with arrows showing directional movement. Colors distinguish individual elevator shafts.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // SIMPLIFIED VIEW
  return (
    <div className="w-full h-full bg-slate-900/50">
      {/* Header */}
      <div className="border-b border-slate-700/50 px-6 py-4 sticky top-0 z-10 bg-slate-900/80 backdrop-blur">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            Traffic & Building Density
          </h1>
          <div className="flex gap-2 bg-slate-800/50 p-1 rounded-lg border border-slate-700/50">
            <button className="px-4 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded font-medium text-sm">
              Simplified
            </button>
            <button
              onClick={() => setView("detailed")}
              className="px-4 py-1.5 text-slate-400 hover:text-white rounded font-medium text-sm transition"
            >
              Detailed
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6 overflow-auto">
        {/* Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map((metric, index) => (
            <div
              key={index}
              className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-5 hover:bg-slate-800 hover:border-slate-600 transition-all group"
            >
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  {metric.label}
                </span>
                <span className="text-xl group-hover:scale-110 transition-transform">
                  {metric.icon}
                </span>
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <div className="text-3xl font-bold text-white">
                  {metric.value}
                </div>
                {metric.unit && (
                  <span className="text-sm text-slate-400">{metric.unit}</span>
                )}
              </div>
              {metric.change && (
                <div
                  className={`text-xs font-semibold px-2 py-1 rounded inline-block ${
                    metric.change.isPositive
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "bg-red-500/15 text-red-400"
                  }`}
                >
                  {metric.change.isPositive ? "+" : ""}
                  {metric.change.percent}% vs yesterday
                </div>
              )}
              {metric.subtext && (
                <div className="text-xs text-slate-400 mt-2">{metric.subtext}</div>
              )}
            </div>
          ))}
        </div>

        {/* ===== ELEVATOR TRAFFIC ANALYTICS ===== */}

        {/* Date/Time Filter Bar & Print */}
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">From</label>
              <input
                type="datetime-local"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="bg-slate-700/50 border border-slate-600 rounded px-3 py-1.5 text-sm text-white focus:border-cyan-500 focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">To</label>
              <input
                type="datetime-local"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="bg-slate-700/50 border border-slate-600 rounded px-3 py-1.5 text-sm text-white focus:border-cyan-500 focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Elevator</label>
              <select
                value={filterElevator}
                onChange={(e) => setFilterElevator(e.target.value)}
                className="bg-slate-700/50 border border-slate-600 rounded px-3 py-1.5 text-sm text-white focus:border-cyan-500 focus:outline-none"
              >
                <option value="all">All Elevators</option>
                {elevatorTrafficData
                  .map((e) => e.elevatorId)
                  .filter((id, idx, arr) => arr.indexOf(id) === idx)
                  .map((eid) => (
                    <option key={eid} value={eid}>{eid}</option>
                  ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Floor</label>
              <select
                value={filterFloor}
                onChange={(e) => setFilterFloor(e.target.value)}
                className="bg-slate-700/50 border border-slate-600 rounded px-3 py-1.5 text-sm text-white focus:border-cyan-500 focus:outline-none"
              >
                <option value="all">All Floors</option>
                {elevatorTrafficData
                  .map((e) => e.floor)
                  .filter((f, idx, arr) => arr.indexOf(f) === idx)
                  .sort((a, b) => a - b)
                  .map((f) => (
                    <option key={f} value={f}>Floor {f}</option>
                  ))}
              </select>
            </div>
            <div className="flex-1"></div>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded font-medium text-sm hover:from-cyan-400 hover:to-blue-400 transition"
            >
              <span>📥</span> Export CSV
            </button>
          </div>
        </div>

        {/* Traffic Event Log */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-white mb-1">Traffic Event Log</h2>
            <p className="text-sm text-slate-400">Detailed records — filterable and exportable</p>
          </div>
          <span className="text-3xl">📋</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/80">
              <tr className="border-b border-slate-700/50">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Date & Time</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Elevator</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Floor</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">People In</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">People Out</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">In Elevator</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredTrafficData()
                .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime())
                .slice(0, visibleRows).map((row, i) => {
                const isCritical = row.peopleInElevator > ELEVATOR_CAPACITY;
                const isWarning = row.peopleInElevator > ELEVATOR_CAPACITY * 0.9;
                return (
                  <tr key={i} className={`border-b border-slate-700/30 hover:bg-slate-700/20 transition ${isCritical ? "bg-red-500/5" : ""}`}>
                    <td className="py-3 px-4 text-slate-300 font-mono text-xs">{new Date(row.dateTime).toLocaleString()}</td>
                    <td className="py-3 px-4 text-cyan-300 font-semibold">{row.elevatorId}</td>
                    <td className="py-3 px-4 text-center">
                      <span className="bg-slate-700/50 text-slate-200 px-2 py-0.5 rounded-full text-xs font-semibold">{row.floor}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full text-xs font-semibold">{row.peopleIn}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded-full text-xs font-semibold">{row.peopleOut}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        isCritical ? "bg-red-500/20 text-red-400" :
                        isWarning ? "bg-yellow-500/20 text-yellow-400" :
                        "bg-slate-700/50 text-white"
                      }`}>{row.peopleInElevator}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {isCritical && <span className="text-red-400 text-xs font-bold">🚨 OVERLOAD</span>}
                      {!isCritical && isWarning && <span className="text-yellow-400 text-xs font-bold">⚠ HIGH</span>}
                      {!isCritical && !isWarning && <span className="text-emerald-400 text-xs font-bold">✅ OK</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {(() => {
          const total = getFilteredTrafficData().length;
          if (total <= visibleRows) return null;
          return (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700/50">
              <span className="text-sm text-slate-400">Showing {Math.min(visibleRows, total)} of {total} records</span>
              <button
                onClick={() => setVisibleRows((prev) => Math.min(prev + 10, total))}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition font-semibold"
              >
                Show More
              </button>
            </div>
          );
        })()}

      </div>
    </div>
  );
}
